import React from "react";
//#region types
import "date-fns";
import { MonthRangePickerBaseProps, MonthPickerQuickSelection } from "models/propTypes/datepickerPropTypes";
import { DateRange } from "models/formTypes/util";
import { IconDefinitionConstants } from "utils/constants";
import { TranslationKeys } from "translations";
//#endregion
//#region components
import DatePicker from "react-datepicker";
import Icon from "../icon";
import { Grid, Box, ClickAwayListener, Avatar } from "@mui/material";
//#endregion
import "react-datepicker/dist/react-datepicker.css";
import "theme/custom/react-datepicker.css";
import cssExports from "theme/styles/main.module.scss";
import clsx from "clsx";
import { CSSHelper, DateHelper, LocalizationHelperInstance } from "utils/helpers";

const MonthRangePickerBase: React.FC<MonthRangePickerBaseProps> = (props) => {
  const [isCalenderOpen, setIsCalenderOpen] = React.useState<boolean>(false);
  const [dates, setDates] = React.useState<DateRange | undefined>(props.range);
  const [reloadKey, setReloadKey] = React.useState<number>(0);
  const [minDate, setMinDate] = React.useState<Date | undefined>(props.minDate ? new Date(props.minDate) : undefined);

  const {
    label,
    icon,
    displayType,
    range,
    onChange: onDateChange,
    quickSelections,
    selection,
    actions,
    showBubble,
    showNavigators,
    ...pickerProps
  } = props;

  React.useEffect(() => {
    setDates(props.range);
  }, [props.range]);

  const openCalender = () => {
    setIsCalenderOpen(true);
  };

  const closeCalender = (manual?: boolean) => {
    if (!manual && !dates?.end) {
      const range: DateRange = { ...dates, end: dates?.start ? formatEndDate(dates?.start) : undefined };
      setRange(range);
    }
    setIsCalenderOpen(false);
  };

  const formatStartDate = (date: Date) => {
    const first = DateHelper.getFirstDayOfMonth(date);
    first.setHours(0, 0, 0, 0);
    return first;
  };

  const formatEndDate = (date: Date) => {
    const last = DateHelper.getLastDayOfMonth(date);
    last.setHours(23, 59, 59, 999);
    return last;
  };

  const onChange = (newDate: Date | null, event: React.SyntheticEvent<any, Event> | undefined) => {
    event?.persist();
    event?.stopPropagation();

    const date = newDate ? new Date(newDate) : null;

    let range: DateRange;
    if (dates?.end || !dates?.start) {
      range = { start: date ? formatStartDate(date) : undefined, end: undefined };
    } else if (dates?.start && date) {
      if (dates.start > date) {
        range = { start: formatStartDate(date), end: dates.start };
      } else {
        range = { ...dates, end: formatEndDate(date) };
      }
    } else {
      range = { ...dates, end: date ? formatEndDate(date) : undefined };
    }

    setRange(range);
    if (range.start && range.end) {
      closeCalender(true);
    }
  };

  const setRange = (range: DateRange) => {
    setDates(range);
    setReloadKey(reloadKey + 1);

    if (range.start && !range.end) {
      setMinDate(new Date(range.start));
    } else {
      setMinDate(props.minDate ? new Date(props.minDate) : undefined);
    }

    if (range.start && range.end) {
      const selection: MonthPickerQuickSelection = {
        key: "",
        text: "",
        range: range,
      };
      props.onChange(selection);
    }
  };

  const onCustomOptionSelect = (
    quickSelection: MonthPickerQuickSelection,
    event: React.MouseEvent<HTMLElement, MouseEvent>
  ) => {
    event.stopPropagation();
    if (typeof quickSelection.range !== "function") {
      setDates(quickSelection.range);
    }
    props.onChange(quickSelection);
    setReloadKey(reloadKey + 1);
    closeCalender(true);
  };

  const navigateLeft = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
    event.stopPropagation();
    if (isCalenderOpen) {
      closeCalender(true);
    }
    if (dates?.start) {
      const previousMonth = new Date(dates.start);
      previousMonth.setMonth(previousMonth.getMonth() - 1);
      const newRange: DateRange = {
        start: formatStartDate(previousMonth),
        end: dates.end ? formatEndDate(previousMonth) : undefined
      };
      setRange(newRange);
    }
  };

  const navigateRight = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
    event.stopPropagation();
    if (isCalenderOpen) {
      closeCalender(true);
    }
    if (dates?.start) {
      const nextMonth = new Date(dates.start);
      nextMonth.setMonth(nextMonth.getMonth() + 1);
      const newRange: DateRange = {
        start: formatStartDate(nextMonth),
        end: dates.end ? formatEndDate(nextMonth) : undefined
      };
      setRange(newRange);
    }
  };

  const value = dates ? DateHelper.formatMonthRange(dates.start ?? undefined, dates.end ?? undefined, true) : "";

  return (
    <ClickAwayListener onClickAway={closeCalender.bind(null, false)}>
      <Box
        className={clsx(
          cssExports.datePickerInputOuter,
          CSSHelper.getClassByDisplayType(props.displayType),
          isCalenderOpen ? cssExports.datePickerOpen : "",
          props.disabled ? cssExports.disabledPicker : "",
          props.className
        )}
        onClick={props.disabled ? undefined : openCalender}
      >
        <Grid
          container
          item
          direction="row"
          alignItems="center"
          wrap="nowrap"
          className={cssExports.datePickerInputOuterContent}
        >
          {(props.icon || props.selection?.icon) && (
            <Grid item className={cssExports.datePickerInputIcon}>
              <Icon icon={props.icon ?? props.selection?.icon ?? IconDefinitionConstants.WEmpty} />
            </Grid>
          )}
          <Grid item xs className={cssExports.datePickerInput}>
            <Grid container item className={cssExports.datePickerValue}>
              {value}
            </Grid>
            <Grid container item direction="column" className={cssExports.datePickerInputInnerContainer}>
              <DatePicker
                {...pickerProps}
                key={reloadKey}
                selected={dates?.start && new Date(dates?.start)}
                value={value}
                startDate={dates?.start && new Date(dates?.start)}
                endDate={dates?.end && new Date(dates?.end)}
                maxDate={props.maxDate && new Date(props.maxDate)}
                minDate={minDate}
                dateFormat="MMM yyyy"
                showMonthYearPicker
                onCalendarOpen={openCalender}
                onChange={onChange}
                wrapperClassName={cssExports.monthPicker}
                open={isCalenderOpen}
                allowSameDay
                disabledKeyboardNavigation
                showPopperArrow={showBubble ? true : false}
              >
                <Grid item container className={cssExports.datePickerBubbleAdditionalContent}>
                  {props.quickSelections ? (
                    <Grid container>
                      <Grid item container className={cssExports.datePickerQuickSelectionTitle}>
                        {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Shortcuts)}
                      </Grid>
                      <Grid item container direction="column">
                        {props.quickSelections.map((option) => (
                          <Grid
                            item
                            container
                            direction="row"
                            key={option.key}
                            onClick={onCustomOptionSelect.bind(null, option)}
                            spacing={1}
                            className={props.selection?.key === option.key ? cssExports.selected : ""}
                            wrap="nowrap"
                          >
                            <Grid item className={cssExports.datePickerQuickSelectionIcon}>
                              <Icon icon={option.icon ?? IconDefinitionConstants.WCalendar} />
                            </Grid>
                            <Grid item className={cssExports.datePickerQuickSelectionText}>
                              {option.text}
                            </Grid>
                          </Grid>
                        ))}
                      </Grid>
                    </Grid>
                  ) : (
                    <></>
                  )}
                  {props.actions && (
                    <Grid item container className={cssExports.datePickerActions}>
                      {props.actions.map((action) => (
                        <Grid
                          item
                          container
                          direction="row"
                          key={action.text}
                          onClick={action.onClickHandler}
                          spacing={1}
                          wrap="nowrap"
                        >
                          {action.icon && (
                            <Grid item className={cssExports.datePickerActionIcon}>
                              <Icon icon={action.icon} />
                            </Grid>
                          )}
                          <Grid item className={cssExports.datePickerActionText}>
                            {action.text}
                          </Grid>
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </Grid>
              </DatePicker>
            </Grid>
          </Grid>
          <Grid item className={cssExports.datePickerInputDropIcon}>
            <Icon
              icon={
                isCalenderOpen ? IconDefinitionConstants.WDropArrowUpSmall : IconDefinitionConstants.WDropArrowSmall
              }
            />
          </Grid>
          {showNavigators && (
            <Grid item container className={cssExports.datePickerNavigators}>
              <Grid item>
                <Avatar
                  className={cssExports.datePickerNavigatorButton}
                  onClick={navigateLeft}
                >
                  <Icon icon={IconDefinitionConstants.WChevronSmallLeft} />
                </Avatar>
              </Grid>
              <Grid item>
                <Avatar
                  className={cssExports.datePickerNavigatorButton}
                  onClick={navigateRight}
                >
                  <Icon icon={IconDefinitionConstants.WChevronSmallRight} />
                </Avatar>
              </Grid>
            </Grid>
          )}
        </Grid>
        {showBubble && <Grid container className={cssExports.datePickerWrapperBottom}></Grid>}
      </Box>
    </ClickAwayListener>
  );
};

export default MonthRangePickerBase;
